import type { Edit, VersionedRow } from '@signalvote/data';
import type { RowVersion, TriageItem } from '@signalvote/shared';
import { defineWrite } from '@/lib/data';
import { editInboxRow, withBankLineVersion } from './sortInboxOptimistic';

/** The token each flippable row carries: a bank line's is still a plain number. */
interface FlipToken {
  Contribution: RowVersion;
  Expenditure: RowVersion;
  BankTransaction: number;
}

export type FlippableEntity = keyof FlipToken;

interface FlippableRow<E extends FlippableEntity> {
  matches: (id: string) => (item: TriageItem) => boolean;
  /** Write the returned token into the cached row, for a write that still reads its token there. */
  keepToken?: (id: string, version: FlipToken[E]) => Edit[];
}

const flippableRows: { [E in FlippableEntity]: FlippableRow<E> } = {
  Contribution: {
    matches: (id) => (item) => item.kind === 'Contribution' && item.contribution.id === id,
  },
  Expenditure: {
    matches: (id) => (item) => item.kind === 'Expenditure' && item.expenditure.id === id,
  },
  BankTransaction: {
    matches: (id) => (item) => item.kind === 'BankLine' && item.bt.id === id,
    // A bank line's delete is not a row write yet and sends the cached token.
    keepToken: (id, version) => [editInboxRow((item) => item.kind === 'BankLine' && item.bt.id === id, withBankLineVersion(version))],
  },
};

/** The POST a flip sends, with the row's freshest token. */
export interface FlipEndpoint<E extends FlippableEntity, B, R extends { concurrencyVersion: FlipToken[E] }> {
  entity: E;
  send: (id: string, concurrencyVersion: FlipToken[E], body: B, row: VersionedRow<FlipToken[E]>) => Promise<R>;
}

export interface StatusFlipConfig<E extends FlippableEntity, B, R extends { concurrencyVersion: FlipToken[E] }> {
  endpoint: FlipEndpoint<E, B, R>;
  /** The `mark*` patch mirroring the resolved projection the write-set refetch produces. */
  patch: (item: TriageItem) => TriageItem;
}

/**
 * A ledger status flip: a row write that patches the sort inbox on click. The write door rolls
 * the row back on a failure and the server's write set refreshes the inbox, the filing preflight
 * and finance health. A transient network blip retries quietly; a server answer surfaces at once
 * so its rollback and inline Retry fire immediately.
 */
export function defineStatusFlip<E extends FlippableEntity, B, R extends { concurrencyVersion: FlipToken[E] }>(
  config: StatusFlipConfig<E, B, R>,
) {
  const { endpoint, patch } = config;
  const rows: FlippableRow<E> = flippableRows[endpoint.entity];
  return defineWrite({
    row: endpoint.entity,
    send: endpoint.send,
    retry: 'transient',
    patch: ({ row }) => [editInboxRow(rows.matches(row.id), patch)],
    land: (written, { row }) => rows.keepToken?.(row.id, written.concurrencyVersion) ?? [],
  });
}
